import React, { useEffect, useMemo, useState } from 'react';
import apinode from '../../services/apinode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileZipper, faStore, faCalendar, faMagnifyingGlass,
  faDownload, faFileInvoice, faSpinner, faCircleCheck, faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import Navbar from '../../components/navbar/navbar';

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const p2 = (n) => String(n).padStart(2, '0');

export default function DownloadNotas() {
  const agora = new Date();
  const conta = localStorage.idconta;

  const [apelidos, setApelidos] = useState([]);
  const [idLoja, setIdLoja] = useState('');
  const [ano, setAno] = useState(agora.getFullYear());
  const [mes, setMes] = useState(agora.getMonth() + 1);
  const [incluirTodas, setIncluirTodas] = useState(false);

  const [contagem, setContagem] = useState(null);
  const [contando, setContando] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [msg, setMsg] = useState(null);

  const anos = useMemo(() => {
    const y = agora.getFullYear();
    return Array.from({ length: 6 }, (_, i) => y - i);
  }, [agora]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apinode.get(`/docs/nfe/apelidos/${conta}`);
        setApelidos(data || []);
        if (data?.length) setIdLoja(String(data[0].id_conta));
      } catch (e) {
        setMsg({ tipo: 'err', txt: 'Não foi possível carregar as contas.' });
      }
    })();
  }, [conta]);

  // apelido correspondente ao idLoja escolhido (só pra exibir e nomear o zip)
  const apelidoSel = useMemo(
    () => apelidos.find((a) => String(a.id_conta) === String(idLoja))?.apelido || '',
    [apelidos, idLoja],
  );

  // zera a contagem quando muda o filtro
  useEffect(() => { setContagem(null); setMsg(null); }, [idLoja, ano, mes, incluirTodas]);

  async function conferir() {
    if (!idLoja) return;
    setContando(true); setMsg(null); setContagem(null);
    try {
      const { data } = await apinode.get(`/docs/nfe/contagem/${conta}`, {
        params: { idLoja, ano, mes, todas: incluirTodas ? 1 : undefined },
      });
      setContagem(data.total);
    } catch (e) {
      setMsg({ tipo: 'err', txt: e.response?.data?.message || e.message });
    } finally {
      setContando(false);
    }
  }

  async function baixar() {
    if (!idLoja) return;
    setBaixando(true); setMsg(null);
    try {
      const resp = await apinode.get(`/docs/nfe/download/${conta}`, {
        params: { idLoja, apelido: apelidoSel || undefined, ano, mes, todas: incluirTodas ? 1 : undefined },
        responseType: 'blob',
        timeout: 0, // download longo: sem timeout nesta chamada
      });

      // se o backend responder JSON (erro/404) mesmo pedindo blob
      const tipo = resp.headers['content-type'] || '';
      if (tipo.includes('application/json')) {
        const txt = await resp.data.text();
        const j = JSON.parse(txt);
        throw new Error(j.message || 'Nenhuma nota encontrada.');
      }

      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `xml_${apelidoSel || idLoja}_${ano}-${p2(mes)}.zip`.replace(/[^\w.-]/g, '_');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setMsg({ tipo: 'ok', txt: 'Download iniciado.' });
    } catch (e) {
      setMsg({ tipo: 'err', txt: e.message || 'Erro ao baixar.' });
    } finally {
      setBaixando(false);
    }
  }

  return (
    <>
      <Navbar />
      <style>{CSS}</style>

      <div className="nf-wrap">
        <header className="nf-head">
          <div className="nf-eyebrow">Fiscal</div>
          <h1 className="nf-title"><FontAwesomeIcon icon={faFileZipper} /> Baixar XML de notas fiscais</h1>
          <p className="nf-sub">Selecione a conta e o período. Os XML são empacotados num .zip, nomeados pela chave de acesso.</p>
        </header>

        <div className="nf-card">
          <div className="nf-fields">
            <div className="nf-field">
              <label><FontAwesomeIcon icon={faStore} /> Conta</label>
              <select value={idLoja} onChange={(e) => setIdLoja(e.target.value)}>
                {apelidos.length === 0 && <option value="">—</option>}
                {apelidos.map((a) => <option key={a.id_conta} value={a.id_conta}>{a.apelido}</option>)}
              </select>
            </div>

            <div className="nf-field">
              <label><FontAwesomeIcon icon={faCalendar} /> Ano</label>
              <select value={ano} onChange={(e) => setAno(Number(e.target.value))}>
                {anos.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            <div className="nf-field nf-field--wide">
              <label><FontAwesomeIcon icon={faCalendar} /> Mês</label>
              <select value={mes} onChange={(e) => setMes(Number(e.target.value))}>
                {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
          </div>

          <label className="nf-check">
            <input type="checkbox" checked={incluirTodas} onChange={(e) => setIncluirTodas(e.target.checked)} />
            <span>Incluir notas não transmitidas (por padrão, só as autorizadas — status <b>T</b>)</span>
          </label>

          <div className="nf-actions">
            <button className="nf-btn nf-btn--ghost" onClick={conferir} disabled={!idLoja || contando || baixando}>
              <FontAwesomeIcon icon={contando ? faSpinner : faMagnifyingGlass} spin={contando} />
              {contando ? 'Conferindo…' : 'Conferir quantidade'}
            </button>

            <button className="nf-btn nf-btn--main" onClick={baixar} disabled={!idLoja || baixando || contagem === 0}>
              <FontAwesomeIcon icon={baixando ? faSpinner : faDownload} spin={baixando} />
              {baixando ? 'Gerando .zip…' : 'Baixar XML (.zip)'}
            </button>
          </div>

          {contagem !== null && (
            <div className={`nf-count ${contagem === 0 ? 'zero' : ''}`}>
              <FontAwesomeIcon icon={faFileInvoice} />
              {contagem === 0
                ? 'Nenhuma nota encontrada para esse período.'
                : <><b>{contagem}</b> nota{contagem > 1 ? 's' : ''} {incluirTodas ? '' : 'autorizada' + (contagem > 1 ? 's' : '')} em {MESES[mes - 1]}/{ano} · {apelidoSel}</>}
            </div>
          )}

          {msg && (
            <div className={`nf-msg ${msg.tipo === 'err' ? 'err' : 'ok'}`}>
              <FontAwesomeIcon icon={msg.tipo === 'err' ? faTriangleExclamation : faCircleCheck} /> {msg.txt}
            </div>
          )}
        </div>

        <p className="nf-note">
          Notas grandes podem levar alguns segundos — o .zip é montado em streaming enquanto baixa.
          O XML é gravado exatamente como está no banco, preservando a assinatura digital.
        </p>
      </div>
    </>
  );
}

const CSS = `
.nf-wrap{--ink:#0f172a;--muted:#64748b;--line:#e6eaf0;--teal:#0d9488;
  max-width:720px;margin:0 auto;padding:30px 22px 60px;color:var(--ink);
  font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;}
.nf-wrap *{box-sizing:border-box;}
.nf-eyebrow{font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--teal);}
.nf-title{font-size:25px;font-weight:800;letter-spacing:-.02em;margin:3px 0 0;display:flex;align-items:center;gap:10px;}
.nf-title svg{color:var(--teal);}
.nf-sub{color:var(--muted);font-size:14px;margin:8px 0 22px;line-height:1.5;}

.nf-card{background:#fff;border:1px solid var(--line);border-radius:18px;padding:22px;box-shadow:0 8px 24px rgba(15,23,42,.05);}
.nf-fields{display:grid;grid-template-columns:1.4fr .8fr 1fr;gap:14px;}
@media(max-width:600px){.nf-fields{grid-template-columns:1fr;}}
.nf-field label{display:flex;align-items:center;gap:7px;font-size:12px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.05em;margin-bottom:7px;}
.nf-field label svg{color:#94a3b8;font-size:11px;}
.nf-field select{width:100%;height:44px;border:1px solid var(--line);border-radius:11px;padding:0 12px;font:inherit;font-size:14px;font-weight:600;color:var(--ink);background:#fff;cursor:pointer;outline:none;transition:.12s;}
.nf-field select:focus{border-color:var(--teal);box-shadow:0 0 0 3px rgba(13,148,136,.12);}

.nf-check{display:flex;align-items:center;gap:9px;margin:18px 0 4px;font-size:13px;color:#475569;cursor:pointer;}
.nf-check input{width:16px;height:16px;accent-color:var(--teal);cursor:pointer;}
.nf-check b{color:var(--ink);}

.nf-actions{display:flex;gap:10px;margin-top:20px;flex-wrap:wrap;}
.nf-btn{display:inline-flex;align-items:center;gap:9px;height:46px;padding:0 20px;border-radius:12px;font:inherit;font-size:14px;font-weight:700;cursor:pointer;transition:.13s;border:1px solid transparent;}
.nf-btn:disabled{opacity:.5;cursor:default;}
.nf-btn--ghost{background:#fff;border-color:var(--line);color:#334155;}
.nf-btn--ghost:hover:not(:disabled){border-color:#cbd5e1;}
.nf-btn--main{background:var(--teal);color:#fff;}
.nf-btn--main:hover:not(:disabled){filter:brightness(1.07);}

.nf-count{display:flex;align-items:center;gap:9px;margin-top:18px;padding:13px 15px;border-radius:11px;background:#f0fdfa;color:#0f766e;font-size:14px;border:1px solid #ccfbf1;}
.nf-count.zero{background:#fff7ed;color:#c2410c;border-color:#fed7aa;}
.nf-count b{font-size:16px;}

.nf-msg{display:flex;align-items:center;gap:9px;margin-top:12px;padding:12px 15px;border-radius:11px;font-size:14px;font-weight:600;}
.nf-msg.ok{background:#ecfdf5;color:#047857;}
.nf-msg.err{background:#fff1f3;color:#be123c;}

.nf-note{color:#94a3b8;font-size:12.5px;margin-top:18px;line-height:1.5;}
`;